import AppConfig from '@/AppConfig';
import { Team, User } from '@/classes/auth';
import store, { RootState } from '@/store';
import axios from 'axios';
import { Module } from 'vuex';
import * as Sentry from '@sentry/vue';
import { i18n } from '@/i18n';
import { isTaxLocationValidationError } from '@/Services/PaymentMethod/taxLocationError';

export type ProviderType = 'default' | 'Google';

export interface AuthState {
  // Private token from API v1
  tokenV1: string;
  // Private token from API v2
  token: string;
  // User from API v2
  user: User;
  // Team from API v2
  team: Team;
  providerType: ProviderType;
}

const AuthStore: Module<AuthState, RootState> = {
  namespaced: true,
  state: {
    tokenV1: '',
    token: '',
    user: new User(),
    team: new Team(),
    providerType: 'default',
  } as AuthState,
  mutations: {
    SET_TOKEN_V1(state, token) {
      state.tokenV1 = token;
    },
    SET_TOKEN(state, token) {
      state.token = token;
    },
    SET_PROVIDER_TYPE(state, providerType: ProviderType) {
      state.providerType = providerType;
    },
    SET_USER(state, user) {
      state.user = new User(user);
    },
    SET_TEAM(state, team) {
      state.team = new Team(team);
    },
    SET_LANG(state, lang) {
      state.user.language = lang;
    },
    LOGOUT(state) {
      state.tokenV1 = '';
      state.token = '';
      state.user = new User();
      state.team = new Team();
    },
  },
  actions: {
    async setTokenV1({ commit }, token) {
      await commit('SET_TOKEN_V1', token);
    },
    async setToken({ commit }, token) {
      await commit('SET_TOKEN', token);
    },
    async setProviderType({ commit }, providerType: ProviderType) {
      await commit('SET_PROVIDER_TYPE', providerType);
    },
    async setUser({ commit }, user) {
      await commit('SET_USER', user);
    },
    async setTeam({ commit }, data: any) {
      await commit('SET_TEAM', data);
    },
    async setLang({ commit }, lang) {
      await commit('SET_LANG', lang);
    },
    async logout({ commit }) {
      await commit('LOGOUT');
    },
    async updateTeam({ commit, state }, data: any): Promise<boolean> {
      try {
        await axios.patch(AppConfig.getAPIBaseUrl() + '/teams/' + state.team.id, data);

        // commit the new team data only if the update is successful
        await commit('SET_TEAM', data);

        store.dispatch('layout/setSnackbar', {
          type: 'info',
          icon: 'info-system',
          text: 'Your company info has been updated successfully',
          isSnackbar: false,
          active: true,
        });

        return true;
      } catch (err: any) {
        // Stripe Tax rejected the billing address (422) — show the specific, fixable message
        // instead of the generic failure so the user knows to correct their address.
        const isInvalidTaxLocation = isTaxLocationValidationError(err);

        store
          .dispatch('layout/setSnackbar', {
            type: 'error',
            icon: 'forbidden-system',
            text: isInvalidTaxLocation
              ? i18n.t('fields.checkout.form.invalid_billing_address').toString()
              : 'We failed to update your company info. Please try again or contact support',
            isSnackbar: true,
            active: true,
          })
          .finally(() => store.dispatch('layout/setLoadingIndicator', false));

        // Unexpected failures (not a user-correctable tax-location 422) — report to Sentry.
        if (!isInvalidTaxLocation) {
          Sentry.captureException(err, {
            tags: {
              type: 'API-Error',
              method: 'updateTeam',
            },
          });
        }

        // false so callers (billing/VAT/email modals) can keep the dialog open for correction.
        return false;
      }
    },
  },
  getters: {
    user(state: AuthState): User {
      return state.user;
    },
    team(state: AuthState): Team {
      return state.team;
    },
    isTeamLead(state: AuthState): boolean {
      return !!state.user?.is_team_lead;
    },
  },
};

export default AuthStore;
